import React, { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import useFolderImages from "./hooks/useFolderImages";
import { usePreviewMode } from "./edit/PreviewModeContext";
import "./ImageGallery.css";
import "./edit/edit.css";

async function imageApi(path, options = {}) {
  const res = await fetch(path, { credentials: "include", ...options });
  let data = null;
  const text = await res.text();
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = { error: text };
    }
  }
  if (!res.ok) {
    throw new Error((data && data.error) || `Request failed (${res.status})`);
  }
  return data;
}

function GalleryLightbox({ images, index, onClose, onPrev, onNext }) {
  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose, onPrev, onNext]);

  return (
    <div className="lightbox-overlay" onClick={onClose}>
      <div className="lightbox-container" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="lightbox-close" onClick={onClose}>
          &times;
        </button>
        <button type="button" className="lightbox-nav lightbox-prev" onClick={onPrev}>
          &#8249;
        </button>
        <img
          src={images[index]}
          alt={`Full size ${index + 1}`}
          className="lightbox-image"
        />
        <button type="button" className="lightbox-nav lightbox-next" onClick={onNext}>
          &#8250;
        </button>
        <div className="lightbox-counter">
          {index + 1} / {images.length}
        </div>
      </div>
    </div>
  );
}

function GalleryAddTile({ onUpload, uploading, variant }) {
  const inputRef = useRef(null);

  function handleClick() {
    if (!uploading && inputRef.current) {
      inputRef.current.click();
    }
  }

  function handleChange(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length) onUpload(files);
  }

  return (
    <button
      type="button"
      className={`gallery-add-tile gallery-add-tile--${variant}${uploading ? " uploading" : ""}`}
      onClick={handleClick}
      disabled={uploading}
      aria-label="上传图片"
    >
      <span className="gallery-add-tile-icon">{uploading ? "…" : "+"}</span>
      <input
        ref={inputRef}
        type="file"
        accept=".jpg,.jpeg,.png,.webp,.gif,image/*"
        multiple
        hidden
        onChange={handleChange}
      />
    </button>
  );
}

GalleryAddTile.propTypes = {
  onUpload: PropTypes.func.isRequired,
  uploading: PropTypes.bool,
  variant: PropTypes.oneOf(["strip", "carousel"]).isRequired,
};

function GalleryRemoveBtn({ filename, onDelete }) {
  return (
    <button
      type="button"
      className="gallery-edit-remove"
      aria-label={`删除 ${filename}`}
      onClick={(e) => {
        e.stopPropagation();
        onDelete(filename);
      }}
    >
      &minus;
    </button>
  );
}

GalleryRemoveBtn.propTypes = {
  filename: PropTypes.string.isRequired,
  onDelete: PropTypes.func.isRequired,
};

function ImageGalleryStrip({ images, filenames, editMode, onDelete, onUpload, uploading }) {
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const lightboxPrev = useCallback(() => {
    setLightboxIndex((i) => (i === 0 ? images.length - 1 : i - 1));
  }, [images.length]);
  const lightboxNext = useCallback(() => {
    setLightboxIndex((i) => (i + 1) % images.length);
  }, [images.length]);

  return (
    <>
      <div className="gallery-strip">
        {images.map((img, index) => (
          <div className="gallery-strip-item" key={img}>
            <img
              src={img}
              alt={`Gallery image ${index + 1}`}
              className="gallery-strip-thumb"
              onClick={() => setLightboxIndex(index)}
            />
            {editMode && filenames[index] ? (
              <GalleryRemoveBtn filename={filenames[index]} onDelete={onDelete} />
            ) : null}
          </div>
        ))}
        {editMode ? (
          <GalleryAddTile onUpload={onUpload} uploading={uploading} variant="strip" />
        ) : null}
      </div>
      {lightboxIndex !== null && (
        <GalleryLightbox
          images={images}
          index={lightboxIndex}
          onClose={closeLightbox}
          onPrev={lightboxPrev}
          onNext={lightboxNext}
        />
      )}
    </>
  );
}

ImageGalleryStrip.propTypes = {
  images: PropTypes.arrayOf(PropTypes.string).isRequired,
  filenames: PropTypes.arrayOf(PropTypes.string),
  editMode: PropTypes.bool,
  onDelete: PropTypes.func,
  onUpload: PropTypes.func,
  uploading: PropTypes.bool,
};

function ImageGalleryCarousel({
  images,
  filenames,
  editMode,
  onDelete,
  onUpload,
  uploading,
}) {
  const [state, setState] = useState({ current: 0, prev: 0, direction: 1, transitioning: false });
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const timeoutRef = useRef(null);
  const transitionDuration = 600;

  useEffect(() => {
    if (editMode || state.transitioning || images.length === 0) return;
    timeoutRef.current = setTimeout(() => {
      slideTo((state.current + 1) % images.length, 1);
    }, 3000);
    return () => clearTimeout(timeoutRef.current);
  }, [state, images.length, editMode]);

  useEffect(() => {
    if (state.current >= images.length && images.length > 0) {
      setState((s) => ({ ...s, current: 0, prev: 0, transitioning: false }));
    }
  }, [images.length, state.current]);

  useEffect(() => {
    if (!state.transitioning) return;
    const timer = setTimeout(() => {
      setState((s) => ({ ...s, transitioning: false }));
    }, transitionDuration);
    return () => clearTimeout(timer);
  }, [state.transitioning]);

  function slideTo(idx, dir) {
    if (state.transitioning || idx === state.current || images.length === 0) return;
    setState((s) => ({ current: idx, prev: s.current, direction: dir, transitioning: true }));
    clearTimeout(timeoutRef.current);
  }

  function goLeft() {
    slideTo(state.current === 0 ? images.length - 1 : state.current - 1, -1);
  }
  function goRight() {
    slideTo((state.current + 1) % images.length, 1);
  }

  function openLightbox(index) {
    setLightboxIndex(index);
    setLightboxOpen(true);
    clearTimeout(timeoutRef.current);
  }

  const closeLightbox = useCallback(() => setLightboxOpen(false), []);
  const lightboxPrev = useCallback(() => {
    setLightboxIndex((i) => (i === 0 ? images.length - 1 : i - 1));
  }, [images.length]);
  const lightboxNext = useCallback(() => {
    setLightboxIndex((i) => (i + 1) % images.length);
  }, [images.length]);

  if (editMode && images.length === 0) {
    return (
      <div className="gallery-container gallery-container--preview-empty">
        <GalleryAddTile onUpload={onUpload} uploading={uploading} variant="carousel" />
      </div>
    );
  }

  return (
    <div className={`gallery-container${editMode ? " gallery-container--preview" : ""}`}>
      <div className="gallery-slider-wrapper">
        <button type="button" className="gallery-btn left" onClick={goLeft} disabled={state.transitioning}>
          <span>&lt;</span>
        </button>
        <div className="gallery-slider">
          {images.map((img, idx) => {
            let className = "gallery-image";
            if (idx === state.current) className += " active";
            else if (idx === state.prev) className += state.direction === 1 ? " slide-left" : " slide-right";
            else className += state.direction === 1 ? " slide-right" : " slide-left";
            return (
              <img
                key={img}
                src={img}
                alt={`Slide ${idx + 1}`}
                className={className}
                draggable={false}
                onClick={() => openLightbox(idx)}
                style={{ cursor: "pointer" }}
              />
            );
          })}
          {editMode && filenames[state.current] ? (
            <GalleryRemoveBtn filename={filenames[state.current]} onDelete={onDelete} />
          ) : null}
        </div>
        <button type="button" className="gallery-btn right" onClick={goRight} disabled={state.transitioning}>
          <span>&gt;</span>
        </button>
        {editMode ? (
          <GalleryAddTile onUpload={onUpload} uploading={uploading} variant="carousel" />
        ) : null}
      </div>
      <div className="gallery-dots">
        {images.map((_, idx) => (
          <span
            key={idx}
            className={`gallery-dot${state.current === idx ? " active" : ""}`}
            onClick={() => slideTo(idx, idx > state.current ? 1 : -1)}
          />
        ))}
      </div>
      {lightboxOpen && (
        <GalleryLightbox
          images={images}
          index={lightboxIndex}
          onClose={closeLightbox}
          onPrev={lightboxPrev}
          onNext={lightboxNext}
        />
      )}
    </div>
  );
}

ImageGalleryCarousel.propTypes = {
  images: PropTypes.arrayOf(PropTypes.string).isRequired,
  filenames: PropTypes.arrayOf(PropTypes.string),
  editMode: PropTypes.bool,
  onDelete: PropTypes.func,
  onUpload: PropTypes.func,
  uploading: PropTypes.bool,
};

function ImageGallery({ folder, slotId, variant = "carousel", width = "100%", height = "auto" }) {
  const { isPreview, slots, setSlot, folders } = usePreviewMode();

  const slot = slotId && slots[slotId] && slots[slotId].type === "gallery" ? slots[slotId] : null;
  const effectiveFolder = slot ? slot.folder : folder;

  const { urls: images, filenames, loading, error, refresh } = useFolderImages(effectiveFolder);
  const [uploading, setUploading] = useState(false);
  const [editStatus, setEditStatus] = useState("");
  const [editError, setEditError] = useState("");

  const editMode = Boolean(isPreview && slotId);

  const handleDelete = useCallback(
    async (filename) => {
      if (!window.confirm(`确定删除 ${filename}？`)) return;
      setEditError("");
      setEditStatus("");
      try {
        await imageApi(
          `/api/images/${encodeURIComponent(effectiveFolder)}/${encodeURIComponent(filename)}`,
          { method: "DELETE" }
        );
        setEditStatus(`已删除 ${filename}`);
        refresh();
      } catch (err) {
        setEditError(err.message);
      }
    },
    [effectiveFolder, refresh]
  );

  const handleUpload = useCallback(
    async (files) => {
      if (!files.length) return;
      setUploading(true);
      setEditError("");
      setEditStatus("");
      try {
        const body = new FormData();
        files.forEach((file) => body.append("files", file));
        const result = await imageApi(
          `/api/images/${encodeURIComponent(effectiveFolder)}`,
          { method: "POST", body }
        );
        const count = (result.uploaded || []).length;
        setEditStatus(`已上传 ${count} 张`);
        refresh();
      } catch (err) {
        setEditError(err.message);
      } finally {
        setUploading(false);
      }
    },
    [effectiveFolder, refresh]
  );

  const editProps = editMode
    ? { editMode: true, filenames, onDelete: handleDelete, onUpload: handleUpload, uploading }
    : {};

  let body;
  if (loading) {
    body = <div style={{ textAlign: "center", padding: 24 }}>加载图片中…</div>;
  } else if (error) {
    body = <div style={{ textAlign: "center", padding: 24, color: "#c00" }}>{error}</div>;
  } else if (images.length === 0 && !editMode) {
    body = null;
  } else if (variant === "strip") {
    body = <ImageGalleryStrip images={images} {...editProps} />;
  } else {
    body = <ImageGalleryCarousel images={images} {...editProps} />;
  }

  if (!editMode) {
    return body;
  }

  return (
    <div className="edit-block">
      <div className="edit-gallery-bar">
        <span>图片文件夹：</span>
        <select
          value={effectiveFolder}
          onChange={(e) => setSlot(slotId, { type: "gallery", folder: e.target.value })}
        >
          {(folders.length ? folders : [effectiveFolder]).map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>
      </div>
      {editError ? <p className="gallery-edit-message gallery-edit-error">{editError}</p> : null}
      {editStatus ? <p className="gallery-edit-message">{editStatus}</p> : null}
      {body}
    </div>
  );
}

ImageGallery.propTypes = {
  folder: PropTypes.string.isRequired,
  slotId: PropTypes.string,
  variant: PropTypes.oneOf(["carousel", "strip"]),
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default ImageGallery;
